// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { AccountDetailsGroupsEnum } from '@shared/utils/Enums/AccountDetailsEnum';
import { ApplicationPanelComponentView, ApplicationPanelProps, ApplicationPanelViewModel } from './ApplicationPanelViewModel';
import { AccountDetailsGroupItem } from '@shared/commons/cache/VerticalPanel/AccountDetails/AccountDetailsGroupItem';
import { AccountWidgetAccountDetailsPanelController } from '@shared/commons/AccountWidget/AccountWidgetAccountDetailsPanelController';
import { AccountWidgetAccountsController } from '@shared/commons/AccountWidget/AccountWidgetAccountsController';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { Control } from '../elements/Control';
import { AccountWidgetAssetsController } from '@shared/commons/AccountWidget/AccountWidgetAssetsController';
import { DynProperty } from '@shared/commons/DynProperty';
import { VerticalPanelDataProviderItem } from '../cache/VerticalPanel/VerticalPanelDataProviderItem';
import { AccountAdditionalInfoDestinationPanel } from '@shared/utils/Account/AdditionalInfoItem';
import { DataCache } from '@shared/commons/DataCache';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
export class NewAccountDetailsPanelProps extends ApplicationPanelProps {
}
export class NewAccountDetailsPanelComponentView extends ApplicationPanelComponentView {
    constructor() {
        super(...arguments);
        this.groupItems = [];
    }
}
export class NewAccountDetailsPanelViewModel extends ApplicationPanelViewModel {
    get visible() {
        return this.componentView.visible;
    }
    set visible(value) {
        this.updateComponentView('visible', value);
    }
    get showModalMask() {
        return this.componentView.showModalMask;
    }
    set showModalMask(value) {
        this.updateComponentView('showModalMask', value);
    }
    get left() {
        return this.componentView.left;
    }
    set left(value) {
        this.updateComponentView('left', value);
    }
    get top() {
        return this.componentView.top;
    }
    set top(value) {
        this.updateComponentView('top', value);
    }
    get assetsVisible() {
        return this.componentView.assetsVisible;
    }
    set assetsVisible(value) {
        this.updateComponentView('assetsVisible', value);
    }
    get assets() {
        return this.componentView.assets;
    }
    set assets(value) {
        this.updateComponentView('assets', value);
        this.assetsMenuItems = this.createMenuItems(value);
    }
    get selectedAsset() {
        return this.componentView.selectedAsset;
    }
    set selectedAsset(value) {
        const updated = this.updateComponentView('selectedAsset', value);
        if (updated) {
            for (let i = 0; i < this.assetsMenuItems.length; i++) {
                const assetMenuItem = this.assetsMenuItems[i];
                assetMenuItem.selected = assetMenuItem.value === value;
            }
            this.onSelectedAssetChanged(value);
        }
    }
    get assetsMenuItems() {
        return this.componentView.assetsMenuItems;
    }
    set assetsMenuItems(value) {
        this.updateComponentView('assetsMenuItems', value);
    }
    get groupItems() {
        return this.componentView.groupItems;
    }
    set groupItems(value) {
        this.updateComponentView('groupItems', value);
    }
    constructor() {
        super(NewAccountDetailsPanelComponentView);
        this.detailsGroupItemStateMap = new Map();
        this.arrayGroups = [
            AccountDetailsGroupsEnum.General,
            AccountDetailsGroupsEnum.Margin,
            AccountDetailsGroupsEnum.AccountActivity,
            AccountDetailsGroupsEnum.TodayResult,
            AccountDetailsGroupsEnum.RiskManagement
        ];
        this.onAccountChanged = (newAccount, oldAccount) => {
            if (isNullOrUndefined(newAccount))
                return;
            this.account = newAccount;
            this.getDynamicItems();
            AccountWidgetAssetsController.Account = newAccount;
            const assets = AccountWidgetAssetsController.AssetsArray;
            this.assetsVisible = assets.length > 1;
            this.assets = assets;
            this.selectedAsset = assets[0];
            this.createAccountDetailsGroupItem();
        };
        this.AdditionalProperties = () => {
            const properties = [];
            properties.push(new DynProperty('DetailsGroupItemStateMap', this.mapToJson(this.detailsGroupItemStateMap), DynProperty.STRING, DynProperty.HIDDEN_GROUP));
            return properties;
        };
        this.onUpdate = () => {
            if (!this.visible) {
                return;
            }
            if (isNullOrUndefined(this.groupItems) || this.groupItems.length === 0) {
                return;
            }
            for (const group of this.groupItems) {
                group.onUpDateEmit();
            }
        };
        this.onDetailsGroupItemStateChange = (itemId, isOpened) => {
            this.detailsGroupItemStateMap.set(itemId, isOpened);
            SessionSettings.save();
        };
        this.updateRelation = this.updateRelation.bind(this);
    }
    initialize(props) {
        super.initialize(props);
        this.props = props;
        AccountWidgetAccountDetailsPanelController.isMouseOnPanelHandler = () => {
            return this.isMouseOnPanel();
        };
        this.setDetailsGroupItemState();
        this.createAccountDetailsGroupItem();
        AccountWidgetAccountsController.subscribeOnAccountChanged(this.onAccountChanged);
        AccountWidgetAccountDetailsPanelController.ShowAccountDetailsPanel.Subscribe(this.show, this);
        AccountWidgetAccountDetailsPanelController.HideAccountDetailsPanel.Subscribe(this.hide, this);
        this.onAccountChanged(AccountWidgetAccountsController.Account, null);
        AccountWidgetAccountDetailsPanelController.onAccountLinkOut.Subscribe(this.accountLink_Out, this);
        AccountWidgetAccountDetailsPanelController.onAssetSet.Subscribe(this.selectAsset, this);
        SessionSettings.AdditionalPropertiesHandlers.push(this.AdditionalProperties);
        Control.Ticker.Subscribe(this.onUpdate, this);
    }
    dispose() {
        AccountWidgetAccountsController.unsubscribeOnAccountChanged(this.onAccountChanged);
        AccountWidgetAccountDetailsPanelController.ShowAccountDetailsPanel.UnSubscribe(this.show, this);
        AccountWidgetAccountDetailsPanelController.HideAccountDetailsPanel.UnSubscribe(this.hide, this);
        AccountWidgetAccountDetailsPanelController.onAccountLinkOut.UnSubscribe(this.accountLink_Out, this);
        AccountWidgetAccountDetailsPanelController.onAssetSet.UnSubscribe(this.selectAsset, this);
        Control.Ticker.UnSubscribe(this.onUpdate, this);
        this.props.unsubscribeOnUpdateRelation(this.updateRelation);
        const additionalPropertyIndex = SessionSettings.AdditionalPropertiesHandlers.indexOf(this.AdditionalProperties);
        if (additionalPropertyIndex !== -1) {
            SessionSettings.AdditionalPropertiesHandlers.splice(additionalPropertyIndex, 1);
        }
        super.dispose();
    }
    update(props) {
        super.update(props);
        this.props = props;
    }
    accountDetailsItemDown(context, typePointer) {
        AccountWidgetAccountDetailsPanelController.onTableMouseDown(typePointer);
    }
    accountDetailsItemMove(context) {
        AccountWidgetAccountDetailsPanelController.onTableMouseMove(context);
    }
    accountDetailsItemUp(context) {
        AccountWidgetAccountDetailsPanelController.onTableMouseUP(context);
    }
    hide() {
        if (!this.visible) {
            return;
        }
        AccountWidgetAccountDetailsPanelController.onPanelClose();
        this.visible = false;
        this.showModalMask = false;
        hideContextMenu();
    }
    show(coordinates) {
        AccountWidgetAccountDetailsPanelController.onPanelOpen();
        this.createAccountDetailsGroupItem();
        this.visible = true;
        this.showModalMask = true;
        this.left = coordinates.positionX;
        this.top = coordinates.positionY;
    }
    updateRelation() {
        if (!isNullOrUndefined(this.props.accountId)) {
            this.accountLink_In(this.props.accountId);
        }
        if (!isNullOrUndefined(this.props.detailsGroupItemStateSetting)) {
            this.setSavedSetting(this.props.detailsGroupItemStateSetting);
        }
    }
    isShowed() {
        return this.visible;
    }
    mouseOver(containerContext) {
        this.mouseOnPanel = true;
    }
    mouseLeave(containerContext) {
        this.mouseOnPanel = false;
    }
    setSavedSetting(propertie) {
        const DetailsGroupItemStateMap = this.jsonToMap(propertie);
        if (!isNullOrUndefined(DetailsGroupItemStateMap)) {
            this.detailsGroupItemStateMap = DetailsGroupItemStateMap;
            this.createAccountDetailsGroupItem();
        }
    }
    accountLink_Out(newSubscriber, account, sameAccs) {
        if (!account) {
            if (!AccountWidgetAssetsController.Account) {
                return;
            }
            account = AccountWidgetAssetsController.Account;
        }
        this.accountLink_Out(newSubscriber, account);
        if (!sameAccs) {
            SessionSettings.save();
        }
    }
    accountLink_In(accId) {
        AccountWidgetAccountsController.Account = DataCache.GetAccountById(accId);
        return true;
    }
    hasAccountLookup() {
        return false;
    }
    createMenuItems(assets) {
        const arr = [];
        for (const asset of assets) {
            arr.push({
                type: NewContextMenuItemType.Default,
                text: asset.Name,
                value: asset,
                event: (item) => {
                    this.selectAsset(item.value.Id);
                }
            });
        }
        const funcSort = function (a, b) {
            if (a.text.toLowerCase() > b.text.toLowerCase()) {
                return 1;
            }
            else {
                return -1;
            }
        };
        arr.sort(funcSort);
        return arr;
    }
    selectAsset(assetId) {
        const assets = this.assets;
        for (const asset of assets) {
            if (asset.Id === assetId) {
                this.selectedAsset = asset;
                return;
            }
        }
    }
    onSelectedAssetChanged(asset) {
        if (isNullOrUndefined(asset)) {
            return;
        }
        const account = AccountWidgetAccountsController.Account;
        let assetBalance = null;
        if (AccountWidgetAssetsController.AssetsArray.length > 1) {
            assetBalance = account.assetBalances[asset.Name];
        }
        if (!isNullOrUndefined(asset)) {
            AccountWidgetAccountDetailsPanelController.setDataSourceAsset(assetBalance);
            AccountWidgetAssetsController.SelectedAsset = asset;
        }
    }
    isMouseOnPanel() {
        return this.mouseOnPanel;
    }
    createAccountDetailsGroupItem() {
        const dataSource = AccountWidgetAccountDetailsPanelController.DataSource;
        this.groupItems = [];
        if (isNullOrUndefined(dataSource)) {
            return null;
        }
        for (const type of this.arrayGroups) {
            const controller = dataSource.getGroupController(type);
            const isOpenGroup = this.getValueDetailsGroupItemState(type);
            this.groupItems.push(new AccountDetailsGroupItem(type, controller, isOpenGroup));
        }
        this.subscribeGroups();
    }
    subscribeGroups() {
        if (isNullOrUndefined(this.groupItems) || this.groupItems.length === 0) {
            return;
        }
        for (const group of this.groupItems) {
            group.subscribeChangeOpenedStateEmit(this.onDetailsGroupItemStateChange);
        }
    }
    getDynamicItems() {
        var _a;
        const res = [];
        const account = this.account;
        if (isNullOrUndefined(account)) {
            return;
        }
        const accountAdditionalInfo = account.AccountAdditionalInfo;
        if (!isNullOrUndefined(accountAdditionalInfo)) {
            for (let accountAdditionalInfoItem in accountAdditionalInfo) {
                accountAdditionalInfoItem = accountAdditionalInfo[accountAdditionalInfoItem];
                const item = this.getAdditinalInfoItem(accountAdditionalInfoItem, this.accountDinamicProperties);
                if (item !== null) {
                    res.push(item);
                }
            }
        }
        const assetBalance = account.assetBalances[(_a = this.selectedAsset) === null || _a === void 0 ? void 0 : _a.Name];
        if (!isNullOrUndefined(assetBalance)) {
            const assetAdditionalInfo = assetBalance.AssetAdditionalInfo;
            if (!isNullOrUndefined(assetAdditionalInfo)) {
                for (let assetAdditionalInfoItem in assetAdditionalInfo) {
                    assetAdditionalInfoItem = assetAdditionalInfo[assetAdditionalInfoItem];
                    const item = this.getAdditinalInfoItem(assetAdditionalInfoItem, this.accountDinamicProperties);
                    if (item !== null) {
                        res.push(item);
                    }
                }
            }
        }
        res.sort(function (a, b) {
            return a.SortIndex - b.SortIndex;
        });
        return res;
    }
    getAdditinalInfoItem(accountAdditionalInfoItem, accountDinamicProperties) {
        if (accountAdditionalInfoItem.Destination === AccountAdditionalInfoDestinationPanel.AllPanels ||
            accountAdditionalInfoItem.Destination === AccountAdditionalInfoDestinationPanel.AccountsDetails) {
            const item = new VerticalPanelDataProviderItem();
            item.Group = accountAdditionalInfoItem.GroupInfo;
            item.GroupIndex = accountAdditionalInfoItem.GroupIndex;
            item.SortIndex = accountAdditionalInfoItem.SortIndex; // sort index inside group
            item.LocalizationKey = accountAdditionalInfoItem.NameKey;
            item.Id = accountAdditionalInfoItem.APIKey;
            item.tooltipKey = accountAdditionalInfoItem.ToolTipKey;
            item.visible = accountAdditionalInfoItem.Visible;
            if (!isNullOrUndefined(accountDinamicProperties)) {
                // copy properties
                const baseSesionItem = accountDinamicProperties[accountAdditionalInfoItem.APIKey];
                if (!isNullOrUndefined(baseSesionItem)) {
                    item.Apply(baseSesionItem);
                }
                accountDinamicProperties[accountAdditionalInfoItem.APIKey] = item;
            }
            return item;
        }
    }
    setDetailsGroupItemState() {
        if (this.detailsGroupItemStateMap.size === 0) {
            for (const id of this.arrayGroups) {
                this.detailsGroupItemStateMap.set(id, true);
            }
        }
    }
    getValueDetailsGroupItemState(itemId) {
        return this.detailsGroupItemStateMap.get(itemId);
    }
    mapToJson(map) {
        const obj = Object.fromEntries(map);
        return JSON.stringify(obj);
    }
    jsonToMap(jsonStr) {
        if (!isValidString(jsonStr)) {
            return null;
        }
        const obj = JSON.parse(jsonStr);
        const entriesObj = Object.entries(obj);
        const map = new Map();
        for (const [key, value] of entriesObj) {
            const tKey = Number(key);
            const tValue = Boolean(value);
            map.set(tKey, tValue);
        }
        return map;
    }
}
